import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Radio, Server, FolderCog, Send, RefreshCw, KeyRound, Copy, Loader2,
  CheckCircle2, XCircle, Clock, Download, Trash2, RotateCcw, Music2, ShieldOff,
} from "lucide-react";
import { api } from "@/lib/api";
import { appConfirm } from "@/components/AppDialogHost";

const KIND_LABEL = { bulletin: "News Bulletin", voicetrack: "Voice Track", sweeper: "Sweeper / Imaging", ad: "Commercial" };
const KIND_COLOR = {
  bulletin: "border-sky-500/50 text-sky-300", voicetrack: "border-emerald-500/50 text-emerald-300",
  sweeper: "border-brand-amber/50 text-brand-amber", ad: "border-fuchsia-500/50 text-fuchsia-300",
};
const STATUS_META = {
  queued:      { cls: "bg-bg-elevated text-ink-secondary border-line", Icon: Clock },
  downloading: { cls: "bg-sky-500/15 text-sky-300 border-sky-500/40", Icon: Download },
  delivered:   { cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40", Icon: CheckCircle2 },
  failed:      { cls: "bg-red-500/15 text-red-300 border-red-500/40", Icon: XCircle },
  cancelled:   { cls: "bg-bg-elevated text-ink-muted border-line", Icon: XCircle },
};

const Card = ({ title, kicker, children, right }) => (
  <div className="rounded-xl border border-line bg-bg-surface p-5">
    <div className="flex items-start justify-between mb-3">
      <div>
        <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-ink-muted">{kicker}</div>
        <div className="text-base font-semibold mt-0.5">{title}</div>
      </div>
      {right}
    </div>
    {children}
  </div>
);

export default function SamBroadcaster() {
  const [tab, setTab] = useState("overview");
  const [status, setStatus] = useState(null);
  const [stream, setStream] = useState(null);
  const [tracks, setTracks] = useState([]);
  const [content, setContent] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [deliveries, setDeliveries] = useState([]);
  const [folders, setFolders] = useState(null);
  const [pairToken, setPairToken] = useState("");
  const [busy, setBusy] = useState("");
  const [lib, setLib] = useState(null);
  const [libQ, setLibQ] = useState("");
  const [history, setHistory] = useState([]);

  const loadStatus = useCallback(async () => {
    try { setStatus((await api.get("/sam/status")).data); } catch { /* empty */ }
  }, []);
  const loadStream = useCallback(async () => {
    try { setStream((await api.get("/nowplaying/stream-status")).data); } catch { setStream({ configured: false }); }
    try { setTracks((await api.get("/nowplaying/tracks", { params: { limit: 8 } })).data || []); } catch { /* empty */ }
  }, []);
  const loadDeliveries = useCallback(async () => {
    try { setDeliveries((await api.get("/sam/deliveries")).data.items || []); } catch { /* empty */ }
  }, []);

  useEffect(() => { loadStatus(); loadStream(); loadDeliveries(); }, [loadStatus, loadStream, loadDeliveries]);
  useEffect(() => {
    const t = setInterval(() => { loadStatus(); if (tab === "deliveries") loadDeliveries(); }, 12000);
    return () => clearInterval(t);
  }, [tab, loadStatus, loadDeliveries]);
  useEffect(() => {
    if (tab === "send" && !content.length) api.get("/sam/content").then((r) => setContent(r.data.items || [])).catch(() => {});
    if (tab === "setup" && !folders) api.get("/sam/settings").then((r) => setFolders(r.data.folders)).catch(() => {});
    if (tab === "library" && lib === null) loadLibrary("");
  }, [tab, content.length, folders]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadLibrary = async (q) => {
    try {
      const r = await api.get("/sam/library", { params: { q } });
      setLib(r.data);
      setHistory((await api.get("/sam/history", { params: { limit: 30 } })).data.items || []);
    } catch { setLib({ items: [], total: 0 }); }
  };

  const downloadAgent = async () => {
    if (status?.agent && !(await appConfirm("Downloading a fresh agent generates a new token and disconnects the currently paired one. Continue?"))) return;
    setBusy("download");
    try {
      const r = await api.get("/sam/agent/download", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = "broadcastflow_sam_agent.py"; a.click();
      URL.revokeObjectURL(url);
      toast.success("Agent downloaded — run it on your SAM computer");
      loadStatus();
    } catch { toast.error("Download failed"); }
    setBusy("");
  };

  const queueSelected = async () => {
    const items = content.filter((c) => selected.has(c.asset_id + c.kind))
      .map((c) => ({ kind: c.kind, title: c.title, asset_id: c.asset_id }));
    if (!items.length) return;
    setBusy("queue");
    try {
      await api.post("/sam/deliveries", { items });
      toast.success(`${items.length} item${items.length > 1 ? "s" : ""} queued for SAM`);
      setSelected(new Set());
      loadDeliveries(); loadStatus();
      setTab("deliveries");
    } catch (e) { toast.error(e.response?.data?.detail || "Queue failed"); }
    setBusy("");
  };

  const pair = async () => {
    if (status?.agent && !(await appConfirm("Generate a new pairing token? The current companion app will be disconnected and must be re-paired."))) return;
    setBusy("pair");
    try {
      const r = await api.post("/sam/agent/pair");
      setPairToken(r.data.token);
      loadStatus();
    } catch { toast.error("Pairing failed"); }
    setBusy("");
  };

  const saveFolders = async () => {
    setBusy("folders");
    try {
      await api.put("/sam/settings", { folders });
      toast.success("Folder mappings saved");
    } catch { toast.error("Save failed"); }
    setBusy("");
  };

  const agentOnline = status?.agent_online;
  const counts = status?.delivery_counts || {};

  return (
    <div className="space-y-6" data-testid="sam-broadcaster-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Server className="h-6 w-6 text-brand-amber" /> SAM Broadcaster</h1>
          <p className="text-sm text-ink-muted mt-1">Connect your SAM Broadcaster PRO machine — stream health, content delivery, and library sync.</p>
        </div>
      </div>

      <div className="flex items-center gap-2 rounded-lg border border-line bg-bg-surface px-4 py-2.5 text-xs text-ink-secondary">
        <ShieldOff className="h-4 w-4 text-ink-muted shrink-0" />
        Broadcast control (PAL scripts / queue actions) is intentionally disabled until it's tested against your installed SAM version. This integration only monitors and delivers files.
      </div>

      <div className="flex gap-2">
        {[["overview", "Overview"], ["send", "Send Content"], ["deliveries", "Deliveries"], ["library", "SAM Library"], ["setup", "Setup"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`sam-tab-${k}`}
            className={`rounded-full px-4 py-1.5 text-xs font-mono uppercase tracking-wider border transition ${
              tab === k ? "bg-brand-amber text-black border-brand-amber font-bold" : "border-line text-ink-secondary hover:text-ink-primary"}`}>
            {l}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="grid md:grid-cols-2 gap-4" data-testid="sam-overview">
          <Card kicker="Stream" title="SHOUTcast / Stream Health"
            right={<Radio className={`h-5 w-5 ${stream?.status === "online" ? "text-emerald-400" : "text-ink-muted"}`} />}>
            {!stream ? <Loader2 className="h-4 w-4 animate-spin text-ink-muted" /> : !stream.configured ? (
              <div className="text-sm text-ink-muted">
                No stream URL configured yet. Set your SHOUTcast/Icecast URL in{" "}
                <a href="/dashboard/nowplaying" className="text-brand-amber underline">Now Playing → Rules & Setup</a> — SAM feeds that same stream.
              </div>
            ) : (
              <div className="space-y-2 text-sm" data-testid="sam-stream-health">
                <div className="flex items-center gap-2">
                  <span className={`h-2 w-2 rounded-full ${stream.status === "online" ? "bg-emerald-400" : "bg-red-400"}`} />
                  <span className="font-semibold capitalize">{stream.status || "unknown"}</span>
                  {stream.provider && <span className="text-[10px] font-mono uppercase text-ink-muted border border-line rounded-full px-2 py-0.5">{stream.provider}</span>}
                </div>
                {stream.now_playing && <div className="text-ink-secondary truncate">♪ {stream.now_playing}</div>}
                {stream.listeners != null && <div className="text-xs font-mono text-ink-muted">{stream.listeners} listeners</div>}
                {stream.checked_at && <div className="text-[10px] font-mono text-ink-muted">Last check: {new Date(stream.checked_at).toLocaleTimeString()}</div>}
              </div>
            )}
          </Card>

          <Card kicker="Companion App" title="SAM Computer Agent"
            right={<span className={`rounded-full border px-2.5 py-0.5 text-[10px] font-bold uppercase ${
              agentOnline ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/40"
                : status?.agent ? "bg-red-500/15 text-red-300 border-red-500/40" : "bg-bg-elevated text-ink-muted border-line"}`}
              data-testid="sam-agent-status">
              {agentOnline ? "Online" : status?.agent ? "Offline" : "Not paired"}
            </span>}>
            {status?.agent ? (
              <div className="space-y-1.5 text-sm">
                <div className="text-ink-secondary">{status.agent.hostname || "Awaiting first heartbeat…"}</div>
                {status.agent.sam_info && <div className="text-xs text-ink-muted">{status.agent.sam_info}</div>}
                <div className="text-[10px] font-mono text-ink-muted">
                  Last seen: {status.agent.last_seen_at ? new Date(status.agent.last_seen_at).toLocaleString() : "never"}
                  {status.agent.agent_version ? ` · v${status.agent.agent_version}` : ""}
                </div>
              </div>
            ) : (
              <div className="text-sm text-ink-muted">No companion app paired yet. Pair one in <button onClick={() => setTab("setup")} className="text-brand-amber underline">Setup</button> to start delivering files to this station's SAM computer.</div>
            )}
            <div className="flex gap-3 mt-3 text-[11px] font-mono text-ink-muted">
              <span>Queued: {counts.queued || 0}</span>
              <span className="text-emerald-400">Delivered: {counts.delivered || 0}</span>
              <span className="text-red-400">Failed: {counts.failed || 0}</span>
            </div>
          </Card>

          <Card kicker="Detected from stream" title="Recent Tracks" right={<Music2 className="h-5 w-5 text-ink-muted" />}>
            {!tracks.length ? (
              <div className="text-sm text-ink-muted">No tracks detected yet — they appear here once Now Playing polling sees track changes on your stream.</div>
            ) : (
              <div className="divide-y divide-line" data-testid="sam-recent-tracks">
                {tracks.map((t, i) => (
                  <div key={i} className="py-1.5 flex items-center justify-between gap-3 text-sm">
                    <span className="truncate">{t.title}{t.artist ? ` — ${t.artist}` : ""}</span>
                    <span className="text-[10px] font-mono text-ink-muted shrink-0">
                      {t.detected_at ? new Date(t.detected_at).toLocaleTimeString() : ""}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </Card>

          <Card kicker="Via companion agent" title="SAM Library & Play History">
            <div className="text-sm text-ink-muted leading-relaxed">
              The companion app reads SAM's database <span className="text-ink-secondary">locally</span> (read-only) — both <span className="text-ink-secondary">Firebird</span> (SAM's default) and <span className="text-ink-secondary">MySQL</span> are supported — and syncs your music library and play history to the <button onClick={() => setTab("library")} className="text-brand-amber underline">SAM Library</button> tab. No router changes, credentials never leave your studio. Enable it in the agent's <code className="text-xs">samagent.ini</code>.
            </div>
          </Card>
        </div>
      )}

      {tab === "library" && (
        <div className="space-y-4" data-testid="sam-library-tab">
          <Card kicker={lib?.synced_at ? `Synced ${new Date(lib.synced_at).toLocaleString()}` : "Not synced yet"}
            title={`SAM Music Library ${lib?.total ? `(${lib.total} tracks)` : ""}`}
            right={<button onClick={() => loadLibrary(libQ)} data-testid="sam-library-refresh" className="text-ink-muted hover:text-brand-amber transition"><RefreshCw className="h-4 w-4" /></button>}>
            {!lib ? <Loader2 className="h-4 w-4 animate-spin text-ink-muted" /> : !lib.total ? (
              <div className="text-sm text-ink-muted leading-relaxed py-4">
                No library synced yet. On your SAM computer: run the companion agent, install a database driver — <code className="text-xs">pip install firebird-driver</code> (SAM's default Firebird database) or <code className="text-xs">pip install pymysql</code> (MySQL installs) — then edit <code className="text-xs">samagent.ini</code> with your database details. The library appears here within ~5 minutes.
              </div>
            ) : (
              <>
                <input value={libQ} onChange={(e) => { setLibQ(e.target.value); loadLibrary(e.target.value); }}
                  placeholder="Search artist, title, album, genre…" data-testid="sam-library-search"
                  className="w-full rounded-md border border-line bg-bg-elevated px-3 py-2 text-sm mb-3 focus:border-brand-amber focus:outline-none" />
                <div className="max-h-[420px] overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-[10px] font-mono uppercase tracking-wider text-ink-muted border-b border-line">
                        <th className="px-2 py-2">Artist</th><th className="px-2 py-2">Title</th>
                        <th className="px-2 py-2 hidden md:table-cell">Album</th>
                        <th className="px-2 py-2 hidden lg:table-cell">Genre</th>
                        <th className="px-2 py-2 text-right">Plays</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-line">
                      {(lib.items || []).map((t, i) => (
                        <tr key={i}>
                          <td className="px-2 py-1.5 text-ink-secondary truncate max-w-[180px]">{t.artist}</td>
                          <td className="px-2 py-1.5 font-semibold truncate max-w-[220px]">{t.title}</td>
                          <td className="px-2 py-1.5 text-ink-muted hidden md:table-cell truncate max-w-[160px]">{t.album}</td>
                          <td className="px-2 py-1.5 text-ink-muted hidden lg:table-cell">{t.genre}</td>
                          <td className="px-2 py-1.5 text-right font-mono text-xs">{t.count_played || 0}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </Card>
          <Card kicker="From SAM's history table" title="Recent Play History">
            {!history.length ? (
              <div className="text-sm text-ink-muted">No play history synced yet.</div>
            ) : (
              <div className="divide-y divide-line max-h-[300px] overflow-y-auto" data-testid="sam-history-list">
                {history.map((p, i) => (
                  <div key={i} className="py-1.5 flex items-center justify-between gap-3 text-sm">
                    <span className="truncate">{p.title}{p.artist ? ` — ${p.artist}` : ""}</span>
                    <span className="text-[10px] font-mono text-ink-muted shrink-0">
                      {p.listeners ? `${p.listeners} listeners · ` : ""}{p.played_at}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      )}

      {tab === "send" && (
        <Card kicker="Approved audio" title="Send Content to SAM"
          right={<button onClick={queueSelected} disabled={!selected.size || busy === "queue"} data-testid="sam-queue-btn"
            className="rounded-full bg-brand-amber text-black px-4 py-1.5 text-xs font-mono uppercase tracking-wider font-bold disabled:opacity-40 hover:brightness-110 transition flex items-center gap-1.5">
            {busy === "queue" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
            Queue {selected.size || ""} for SAM
          </button>}>
          {!content.length ? (
            <div className="text-sm text-ink-muted py-6 text-center">No produced audio found yet. Create bulletins, voice tracks, sweepers or commercials first — they'll appear here.</div>
          ) : (
            <div className="divide-y divide-line max-h-[520px] overflow-y-auto" data-testid="sam-content-list">
              {content.map((c) => {
                const key = c.asset_id + c.kind;
                return (
                  <label key={key} className="flex items-center gap-3 py-2 cursor-pointer hover:bg-bg-elevated px-2 rounded">
                    <input type="checkbox" checked={selected.has(key)} data-testid={`sam-content-check-${c.asset_id}`}
                      onChange={() => setSelected((s) => { const n = new Set(s); n.has(key) ? n.delete(key) : n.add(key); return n; })}
                      className="accent-[#FF8A00] h-3.5 w-3.5" />
                    <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase ${KIND_COLOR[c.kind]}`}>{KIND_LABEL[c.kind]}</span>
                    <span className="text-sm truncate flex-1">{c.title}</span>
                    <span className="text-[10px] font-mono text-ink-muted shrink-0">{c.created_at ? new Date(c.created_at).toLocaleDateString() : ""}</span>
                  </label>
                );
              })}
            </div>
          )}
        </Card>
      )}

      {tab === "deliveries" && (
        <Card kicker="Transfer log" title="Deliveries"
          right={<button onClick={loadDeliveries} data-testid="sam-deliveries-refresh" className="text-ink-muted hover:text-brand-amber transition"><RefreshCw className="h-4 w-4" /></button>}>
          {!deliveries.length ? (
            <div className="text-sm text-ink-muted py-6 text-center">No deliveries yet — queue content from the Send Content tab.</div>
          ) : (
            <div className="divide-y divide-line" data-testid="sam-deliveries-list">
              {deliveries.map((d) => {
                const m = STATUS_META[d.status] || STATUS_META.queued;
                return (
                  <div key={d.delivery_id} className="py-2.5 flex items-center gap-3" data-testid={`sam-delivery-${d.delivery_id}`}>
                    <span className={`shrink-0 rounded-full border px-2.5 py-0.5 text-[10px] font-bold uppercase flex items-center gap-1 ${m.cls}`}>
                      <m.Icon className="h-3 w-3" /> {d.status}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold truncate">{d.title}</div>
                      <div className="text-[10px] font-mono text-ink-muted truncate">
                        {KIND_LABEL[d.kind]} · {d.file_name}
                        {d.error ? <span className="text-red-400"> · {d.error}</span> : ""}
                        {d.delivered_at ? ` · delivered ${new Date(d.delivered_at).toLocaleString()}` : ""}
                      </div>
                    </div>
                    {(d.status === "failed" || d.status === "cancelled") && (
                      <button onClick={async () => { await api.post(`/sam/deliveries/${d.delivery_id}/retry`); loadDeliveries(); }}
                        title="Retry" data-testid={`sam-retry-${d.delivery_id}`} className="text-ink-muted hover:text-brand-amber transition"><RotateCcw className="h-4 w-4" /></button>
                    )}
                    {d.status !== "delivered" && (
                      <button onClick={async () => {
                        if (!(await appConfirm(`Remove "${d.title}" from the delivery queue?`))) return;
                        await api.delete(`/sam/deliveries/${d.delivery_id}`); loadDeliveries();
                      }} title="Cancel / remove" data-testid={`sam-cancel-${d.delivery_id}`} className="text-ink-muted hover:text-red-400 transition"><Trash2 className="h-4 w-4" /></button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      )}

      {tab === "setup" && (
        <div className="grid md:grid-cols-2 gap-4">
          <Card kicker="On the SAM computer" title="Folder Mappings" right={<FolderCog className="h-5 w-5 text-ink-muted" />}>
            {!folders ? <Loader2 className="h-4 w-4 animate-spin text-ink-muted" /> : (
              <div className="space-y-3">
                {Object.entries(KIND_LABEL).map(([k, label]) => (
                  <div key={k}>
                    <div className="text-[10px] font-mono uppercase tracking-wider text-ink-muted mb-1">{label}</div>
                    <input value={folders[k] || ""} onChange={(e) => setFolders((f) => ({ ...f, [k]: e.target.value }))}
                      data-testid={`sam-folder-${k}`} placeholder={`C:\\BroadcastFlow\\${label.replace(/[^\w]/g, "")}`}
                      className="w-full rounded-md border border-line bg-bg-elevated px-3 py-2 text-sm font-mono focus:border-brand-amber focus:outline-none" />
                  </div>
                ))}
                <button onClick={saveFolders} disabled={busy === "folders"} data-testid="sam-save-folders"
                  className="rounded-full bg-brand-amber text-black px-4 py-1.5 text-xs font-mono uppercase tracking-wider font-bold hover:brightness-110 transition">
                  {busy === "folders" ? "Saving…" : "Save mappings"}
                </button>
                <p className="text-[11px] text-ink-muted">The companion app creates these folders if they don't exist. Point SAM's category auto-import at them.</p>
              </div>
            )}
          </Card>

          <Card kicker="Pairing" title="Companion App" right={<KeyRound className="h-5 w-5 text-ink-muted" />}>
            <div className="space-y-3 text-sm">
              <p className="text-ink-muted">Run the companion app on your SAM computer — it downloads queued audio into your folders and (optionally) syncs SAM's library. Your pairing token is baked into the download.</p>
              <button onClick={downloadAgent} disabled={busy === "download"} data-testid="sam-download-agent"
                className="rounded-full bg-brand-amber text-black px-4 py-2 text-xs font-mono uppercase tracking-wider font-bold hover:brightness-110 transition flex items-center gap-2">
                {busy === "download" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                Download Companion Agent
              </button>
              <ol className="text-[12px] text-ink-muted space-y-1 list-decimal ml-4">
                <li>Install Python 3.9+ from <span className="text-ink-secondary">python.org</span> (tick "Add to PATH")</li>
                <li>Put the downloaded file on the SAM computer and double-click it (or <code className="text-[11px]">py broadcastflow_sam_agent.py</code>)</li>
                <li>Leave the window open — files land in your mapped folders within 15 seconds of queuing</li>
                <li>Optional library sync: <code className="text-[11px]">pip install firebird-driver</code> (SAM's default Firebird DB) or <code className="text-[11px]">pip install pymysql</code> (MySQL), then edit <code className="text-[11px]">samagent.ini</code></li>
                <li>Optional .exe: <code className="text-[11px]">pip install pyinstaller && pyinstaller --onefile broadcastflow_sam_agent.py</code></li>
              </ol>
              {pairToken ? (
                <div className="rounded-lg border border-brand-amber/40 bg-bg-elevated p-3">
                  <div className="text-[10px] font-mono uppercase tracking-wider text-brand-amber mb-1">Pairing token — copy it now, it won't be shown again</div>
                  <div className="flex items-center gap-2">
                    <code className="text-xs break-all flex-1" data-testid="sam-pair-token">{pairToken}</code>
                    <button onClick={() => { navigator.clipboard?.writeText(pairToken); toast.success("Token copied"); }}
                      data-testid="sam-copy-token" className="text-ink-muted hover:text-brand-amber transition shrink-0"><Copy className="h-4 w-4" /></button>
                  </div>
                </div>
              ) : null}
              <div className="flex gap-2">
                <button onClick={pair} disabled={busy === "pair"} data-testid="sam-pair-btn"
                  className="rounded-full border border-line px-4 py-1.5 text-xs font-mono uppercase tracking-wider text-ink-secondary hover:text-ink-primary transition">
                  {busy === "pair" ? "Generating…" : "Token only (manual pairing)"}
                </button>
                {status?.agent && (
                  <button onClick={async () => {
                    if (!(await appConfirm("Revoke the paired companion app? Deliveries will stop until you pair again."))) return;
                    await api.delete("/sam/agent"); setPairToken(""); loadStatus(); toast.success("Agent revoked");
                  }} data-testid="sam-revoke-btn"
                    className="rounded-full border border-red-500/40 px-4 py-1.5 text-xs font-mono uppercase tracking-wider text-red-400 hover:bg-red-500/10 transition">
                    Revoke
                  </button>
                )}
              </div>
              <p className="text-[11px] text-ink-muted">
                The agent never runs PAL scripts or touches SAM's queue — it only downloads files and reads library data.
              </p>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
